import { logger } from '@/utils';
import {
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { PipelineConfiguration } from '@mai/types';

import { pipelineConfigToReactFlow, reactFlowToPipelineConfig } from '../utils';

export interface UsePipelineFlowOptions {
  mode?: 'yaml' | 'gui';
  pipelineConfig: PipelineConfiguration | null;
  onPipelineConfigChange: (config: PipelineConfiguration) => void;
  nodePositions?: Map<string, { x: number; y: number }>;
}

export interface UsePipelineFlowResult {
  nodes: Node[];
  edges: Edge[];
  isLoading: boolean;
  error: string | null;
  handleNodesChange: (changes: NodeChange[]) => void;
  handleEdgesChange: (changes: EdgeChange[]) => void;
}

/**
 * Custom hook to manage pipeline configuration and React Flow state
 * Centralizes conversion logic, error handling, and state synchronization
 */
export const usePipelineFlow = ({
  mode = 'yaml',
  pipelineConfig,
  onPipelineConfigChange,
  nodePositions = new Map(),
  onNodePositionsChange,
}: UsePipelineFlowOptions & {
  onNodePositionsChange?: (positions: Map<string, { x: number; y: number }>) => void;
}): UsePipelineFlowResult => {
  // Track structural changes vs config-only changes
  const structuralSignature = useMemo(() => {
    if (!pipelineConfig) return null;

    // Create a signature based on blocks only (exclude connections to prevent layout jumping)
    const blockIds = Object.keys(pipelineConfig.blocks).sort().join(',');

    return blockIds;
  }, [pipelineConfig]);

  // Track connection changes separately (for UI updates without layout recalculation)
  const connectionsSignature = useMemo(() => {
    if (!pipelineConfig) return null;

    return pipelineConfig.connections
      .map(
        (c) =>
          `${c.source_block_id}:${c.source_port}->${c.target_block_id}:${c.target_port}`,
      )
      .sort()
      .join('|');
  }, [pipelineConfig]);

  // Create stable signature for node positions to avoid infinite re-renders
  const nodePositionsSignature = useMemo(() => {
    if (!nodePositions || nodePositions.size === 0) return '';

    return Array.from(nodePositions.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, pos]) => `${id}:${pos.x},${pos.y}`)
      .join('|');
  }, [nodePositions]);

  // Track previous structural signature to detect changes
  const prevStructuralSignatureRef = useRef<string | null>(null);

  // Debounce timer for layout recalculation
  const layoutDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Check if we need to recalculate layout (only for structural changes, debounced)
  const shouldRecalculateLayout = useMemo(() => {
    if (!structuralSignature) return false;

    // First load - only calculate layout if we don't have positions
    if (prevStructuralSignatureRef.current === null) {
      prevStructuralSignatureRef.current = structuralSignature;
      // If we have node positions for all blocks, don't recalculate layout
      if (pipelineConfig && nodePositions.size > 0) {
        const allBlocksHavePositions = Object.keys(pipelineConfig.blocks).every(
          (blockId) => nodePositions.has(blockId),
        );
        if (allBlocksHavePositions) {
          return false; // Use existing positions
        }
      }
      return true; // No positions, calculate layout
    }

    // Structure changed - recalculate layout
    const structureChanged =
      prevStructuralSignatureRef.current !== structuralSignature;
    if (structureChanged) {
      prevStructuralSignatureRef.current = structuralSignature;

      // Clear any pending layout calculation
      if (layoutDebounceTimer.current) {
        clearTimeout(layoutDebounceTimer.current);
        layoutDebounceTimer.current = null;
      }

      return true;
    }

    // Config-only change - skip expensive layout
    return false;
  }, [structuralSignature, nodePositions, pipelineConfig]);

  // Convert pipeline configuration to React Flow format
  const {
    nodes: processedNodes,
    edges: processedEdges,
    error,
  } = useMemo(() => {
    logger.debug(
      'usePipelineFlow: Processing pipeline config:',
      pipelineConfig,
    );

    if (!pipelineConfig) {
      return { nodes: [], edges: [], error: null };
    }

    try {
      // Only use Dagre layout when structure actually changes (blocks added/removed)
      // When shouldRecalculateLayout = false (connection-only changes), no layout runs
      const useDagre = shouldRecalculateLayout;

      const reactFlow = pipelineConfigToReactFlow(pipelineConfig, {
        useDagre,
        nodePositions,
      });

      logger.debug(
        'usePipelineFlow: Conversion successful, nodes:',
        reactFlow.nodes.length,
        'edges:',
        reactFlow.edges.length,
        'usedDagre:',
        useDagre,
      );

      return {
        nodes: reactFlow.nodes,
        edges: reactFlow.edges,
        error: null,
      };
    } catch (error) {
      // Log error for debugging but don't break the UI
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown conversion error';
      logger.warn(
        'Error converting pipeline config to React Flow:',
        errorMessage,
      );

      return {
        nodes: [],
        edges: [],
        error: errorMessage,
      };
    }
  }, [
    pipelineConfig,
    shouldRecalculateLayout,
    nodePositionsSignature,
    connectionsSignature,
  ]);

  // Use React Flow state hooks - initialize with processed nodes/edges
  const [nodes, setNodes, onNodesChange] = useNodesState(processedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(processedEdges);

  // Update React Flow state when pipeline config changes (Dagre runs for structure changes)
  // Use atomic updates to prevent intermediate inconsistent states
  useEffect(() => {
    logger.debug(
      'usePipelineFlow: Pipeline config changed, updating React Flow state with Dagre layout',
      {
        processedNodes: processedNodes.length,
        processedEdges: processedEdges.length,
      },
    );

    // Batch state updates atomically to prevent jumping
    const updateReactFlowState = () => {
      setNodes(processedNodes);
      setEdges(processedEdges);
    };

    // Use requestAnimationFrame to ensure updates happen in a single frame
    requestAnimationFrame(updateReactFlowState);
  }, [processedNodes, processedEdges, setNodes, setEdges]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (layoutDebounceTimer.current) {
        clearTimeout(layoutDebounceTimer.current);
        layoutDebounceTimer.current = null;
      }
    };
  }, []);

  // Convert React Flow state back to pipeline configuration
  const convertToPipelineConfig = useCallback(() => {
    try {
      const result = reactFlowToPipelineConfig(nodes, edges);
      if (result.success && result.data) {
        onPipelineConfigChange(result.data);
      } else {
        logger.warn(
          'Failed to convert React Flow to pipeline config:',
          result.error,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown conversion error';
      logger.warn(
        'Error converting React Flow to pipeline config:',
        errorMessage,
      );
    }
  }, [nodes, edges, onPipelineConfigChange]);

  // Handle node changes (GUI mode only)
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (mode !== 'gui') return;

      // Just update React Flow state, don't sync back to pipeline config for every interaction
      onNodesChange(changes);

      // Capture position changes and update the nodePositions map
      const positionChanges = changes.filter(
        (change) => change.type === 'position' && change.position,
      );
      
      if (positionChanges.length > 0 && onNodePositionsChange) {
        // Update positions for moved nodes
        const updatedPositions = new Map(nodePositions);
        positionChanges.forEach((change) => {
          if (change.type === 'position' && change.position) {
            updatedPositions.set(change.id, {
              x: change.position.x,
              y: change.position.y,
            });
          }
        });
        onNodePositionsChange(updatedPositions);
      }

      // Only sync back for structural changes (add/remove), not position changes
      const hasStructuralChanges = changes.some(
        (change) => change.type === 'add' || change.type === 'remove',
      );

      if (hasStructuralChanges) {
        // Debounce pipeline config updates to prevent rapid successive calls
        if (layoutDebounceTimer.current) {
          clearTimeout(layoutDebounceTimer.current);
        }
        layoutDebounceTimer.current = setTimeout(convertToPipelineConfig, 100);
      }
    },
    [mode, onNodesChange, convertToPipelineConfig, nodePositions, onNodePositionsChange],
  );

  // Handle edge changes (GUI mode only)
  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (mode !== 'gui') return;

      // Update React Flow state
      onEdgesChange(changes);

      // Only sync back for structural changes (add/remove edges), not selection/styling
      const hasStructuralChanges = changes.some(
        (change) => change.type === 'add' || change.type === 'remove',
      );

      if (hasStructuralChanges) {
        // Debounce pipeline config updates to prevent rapid successive calls
        if (layoutDebounceTimer.current) {
          clearTimeout(layoutDebounceTimer.current);
        }
        layoutDebounceTimer.current = setTimeout(convertToPipelineConfig, 100);
      }
    },
    [mode, onEdgesChange, convertToPipelineConfig],
  );

  return {
    nodes,
    edges,
    isLoading: false, // Could be enhanced to track conversion state
    error,
    handleNodesChange,
    handleEdgesChange,
  };
};
